import { getScrollParent } from "../../../../../util/scroll";

const STICKY_CLASS = "sticky-header";
const OFFSET_VAR = "--sticky-header-offset";
const H_OVERFLOW_TOLERANCE = 4;

const noop = () => {};

function getStickyOffset(tableEl) {
    const section = tableEl.closest(".section");

    if (!section) {
        return 0;
    }

    for (const child of section.children) {
        const style = window.getComputedStyle(child);

        if (style.position === "sticky" && parseInt(style.top, 10) === 0) {
            return child.offsetHeight;
        }
    }

    return 0;
}

// First row of the table, but only when it is an actual header row (same cells the
// CSS path pins) — otherwise tables without a header would get their first row frozen.
function getHeaderRow(tableEl) {
    const tbody = tableEl.tBodies[0];
    const row = tbody ? tbody.rows[0] : null;

    if (!row) {
        return null;
    }

    const isHeaderRow = Array.from(row.cells).some(
        (cell) => cell.tagName === "TH" || cell.classList.contains("th"),
    );

    return isHeaderRow ? row : null;
}

const CLONE_CELL_PROPS = [
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "borderTop",
    "borderRight",
    "borderBottom",
    "borderLeft",
    "backgroundColor",
    "color",
    "font",
    "fontWeight",
    "textAlign",
    "verticalAlign",
    "whiteSpace",
];

// Frozen header for horizontally-scrollable tables, where CSS `position: sticky` can't pin to the
// page: a fixed, pointer-events:none clone of the header row on <body>, synced to horizontal scroll.
function createStickyClone(tableEl, wrapper, scrollParent) {
    let enabled = false;
    let bar = null;
    let innerTable = null;

    const cloneOffset = () => {
        const parentTop =
            scrollParent && scrollParent.getBoundingClientRect
                ? Math.max(0, scrollParent.getBoundingClientRect().top)
                : 0;
        return parentTop + getStickyOffset(tableEl);
    };

    const ensureBar = () => {
        if (bar) {
            return;
        }
        bar = document.createElement("div");
        bar.className = "sticky-header-clone";
        bar.style.cssText =
            "position:fixed; overflow:hidden; pointer-events:none; z-index:100; display:none;" +
            "background:#fff;";
        innerTable = document.createElement("table");
        innerTable.style.cssText =
            "border-collapse:collapse; table-layout:fixed; position:relative; margin:0;";
        innerTable.appendChild(document.createElement("tbody"));
        bar.appendChild(innerTable);
        document.body.appendChild(bar);
    };

    const hide = () => {
        if (bar) {
            bar.style.display = "none";
        }
    };

    const rebuild = () => {
        if (!enabled) {
            return;
        }
        const row = getHeaderRow(tableEl);
        if (!row) {
            return;
        }

        ensureBar();

        const tbody = innerTable.tBodies[0];
        tbody.innerHTML = "";

        const clonedRow = row.cloneNode(true);
        const realCells = row.children;
        const cloneCells = clonedRow.children;

        for (let i = 0; i < realCells.length; i++) {
            const real = realCells[i];
            const cell = cloneCells[i];
            const cs = window.getComputedStyle(real);
            const width = real.getBoundingClientRect().width;

            cell.style.boxSizing = "border-box";
            cell.style.width = `${width}px`;
            cell.style.minWidth = `${width}px`;
            cell.style.maxWidth = `${width}px`;

            for (const prop of CLONE_CELL_PROPS) {
                cell.style[prop] = cs[prop];
            }

            if (cs.backgroundColor === "rgba(0, 0, 0, 0)" || !cs.backgroundColor) {
                cell.style.backgroundColor = "#fff";
            }
        }

        tbody.appendChild(clonedRow);
    };

    const reposition = () => {
        if (!enabled || !bar) {
            hide();
            return;
        }
        const row = getHeaderRow(tableEl);
        if (!row) {
            hide();
            return;
        }

        const wrapRect = wrapper.getBoundingClientRect();
        const headRect = row.getBoundingClientRect();
        const offset = cloneOffset();

        const shouldShow =
            headRect.top < offset - 0.5 && wrapRect.bottom > offset + headRect.height;
        if (!shouldShow) {
            hide();
            return;
        }

        bar.style.display = "block";
        bar.style.left = `${wrapRect.left}px`;
        bar.style.top = `${offset}px`;
        bar.style.width = `${wrapper.clientWidth}px`;
        bar.style.height = `${headRect.height}px`;
        innerTable.style.left = `${-wrapper.scrollLeft}px`;
    };

    return {
        setEnabled(value) {
            if (value === enabled) {
                return;
            }
            enabled = value;
            if (!value) {
                hide();
            }
        },
        rebuild,
        reposition,
        destroy() {
            if (bar && bar.parentNode) {
                bar.parentNode.removeChild(bar);
            }
            bar = null;
            innerTable = null;
            enabled = false;
        },
    };
}

// Narrow tables freeze via the `.sticky-header` CSS class; scrollable ones via a floating clone.
export function setupStickyHeader(tableEl, { manageWrapperOverflow = false } = {}) {
    const scrollParent = getScrollParent(tableEl);

    if (!scrollParent) {
        return noop;
    }

    const wrapper = tableEl.parentElement;
    const clone = createStickyClone(tableEl, wrapper, scrollParent);

    let isSticky = false;

    const update = () => {
        const overflowsWidth =
            wrapper && wrapper.scrollWidth - wrapper.clientWidth > H_OVERFLOW_TOLERANCE;

        if (manageWrapperOverflow && wrapper) {
            wrapper.style.overflowX = overflowsWidth ? "auto" : "visible";
        }

        const tallerThanViewport = tableEl.offsetHeight > scrollParent.clientHeight;

        if (overflowsWidth) {
            if (isSticky) {
                isSticky = false;
                tableEl.classList.remove(STICKY_CLASS);
            }
            clone.setEnabled(tallerThanViewport);
            clone.rebuild();
            clone.reposition();
            return;
        }

        clone.setEnabled(false);

        if (tallerThanViewport) {
            tableEl.style.setProperty(OFFSET_VAR, `${getStickyOffset(tableEl)}px`);
        }

        if (tallerThanViewport !== isSticky) {
            isSticky = tallerThanViewport;
            tableEl.classList.toggle(STICKY_CLASS, tallerThanViewport);
        }
    };

    let rafId = null;
    const onScroll = () => {
        if (rafId) {
            return;
        }
        rafId = requestAnimationFrame(() => {
            rafId = null;
            clone.reposition();
        });
    };

    const observer = new ResizeObserver(update);
    observer.observe(tableEl);
    observer.observe(scrollParent);
    if (wrapper) {
        observer.observe(wrapper);
    }
    window.addEventListener("resize", update);
    window.addEventListener("scroll", onScroll, true);

    update();

    return () => {
        observer.disconnect();
        window.removeEventListener("resize", update);
        window.removeEventListener("scroll", onScroll, true);
        if (rafId) {
            cancelAnimationFrame(rafId);
        }
        clone.destroy();
    };
}
